export type StatusTone = "green" | "amber" | "blue" | "slate" | "red";

export interface Metric {
  label: string;
  value: string;
  detail: string;
  tone: StatusTone;
}

export interface Gate {
  label: string;
  value: string;
  tone: StatusTone;
}

export interface EventRow {
  time: string;
  lane: string;
  event: string;
  status: string;
  owner: string;
}

export interface EvidenceRow {
  source: string;
  record: string;
  confidence: string;
  output: string;
  gate: string;
}

export interface ProfileVar {
  name: string;
  value: string;
  source: string;
  state: string;
  tone: StatusTone;
}

export interface RouteStep {
  label: string;
  state: string;
  tone: StatusTone;
}

export interface OttoPublicStatus {
  service: string;
  laneId: string;
  portalUrl: string;
  generatedAt: string;
  counts: {
    inboundEmails: number;
    needsReview: number;
    factsLiveSubmissions: number;
    schedulerAppendedEvents: number;
  };
  inboundByStatus: Record<string, number>;
  recentEvents: Array<{
    eventType: string;
    status: string;
    createdAt: string;
  }>;
  gates: {
    factsWriteEnabled: boolean;
    schedulerWriteEnabled: boolean;
    httpIngestEnabled: boolean;
    adminApiEnabled: boolean;
    aiRouteEnabled: boolean;
    criticalAdjudicatorEnabled: boolean;
    factsDuplicateCheckEnabled: boolean;
    factsProductionUrlConfirmed: boolean;
    wideNetTrainingEnabled: boolean;
    wideNetAutoSubmitEnabled: boolean;
    wideNetFactsWriteEnabled: boolean;
  };
  credentialPosture: {
    factsBaseUrlBinding: boolean;
    factsUserBinding: boolean;
    factsPasswordBinding: boolean;
    schedulerWorkbookShareConfigured: boolean;
    graphAuthConfigured: boolean;
    adminTokenConfigured: boolean;
    supabaseAuthConfigured: boolean;
    httpIngestTokenConfigured: boolean;
  };
  adminAuth: {
    mode: string;
    allowedEmailDomains: string[];
    allowlistConfigured: boolean;
  };
  keyMeProfile: {
    customerCode: string;
    pieces: string;
    pallets: string;
    weight: string;
    freightClass: string;
    dimensions: string;
    readyTime: string;
    closeTime: string;
  };
  warehouses: {
    anaheim: { factsCode: string; name: string; address: string; city: string; state: string; zip: string };
    arizona: { factsCode: string; name: string; address: string; city: string; state: string; zip: string };
  };
  scheduler: {
    tableName: string;
    columnCount: number;
  };
  notifications: {
    provider: "resend";
    from: string;
    sendEnabled: boolean;
    defaultToConfigured: boolean;
    resendConfigured: boolean;
    digestWindowMinutes: number;
  };
  warnings: string[];
}

export interface TrialPreview {
  bol: string;
  keyMeId: string;
  action: string;
  site: string;
  route: string;
  freight: string;
  scheduler: string;
}

export interface ReviewShipmentJob {
  source: "hassett-keyme" | "hassett-special-action";
  customerCode: string;
  dedupeKey: string;
  poNumber: string;
  workOrderId: string;
  keyMeId: string;
  task: string;
  siteName: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  storePhone: string;
  storeContact: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  storeHours: string;
  earliestDate: string;
  latestDate: string;
  marketEta: string;
  suggestedServiceDate: string;
  weekEndingDate: string;
  scheduledDateConfirmed: boolean;
  serialNumber: string;
  keyBox: string;
  jobNotes: string;
  scheduleUrl: string;
  instructionGuideUrl: string;
  airtableUrl: string;
  serviceLinePhone: string;
  serviceLineInstructions: string;
  submissionPortal: string;
  confidence: number;
  missingRequired: string[];
  rawSubject: string;
  rawFrom: string;
  receivedAt: string;
  family?: string;
  action?: string;
  externalId?: string;
  subjectLocationCode?: string;
  machineId?: string;
  freightDescription?: string;
  referenceHints?: Array<Record<string, unknown>>;
  productGate?: Record<string, unknown>;
  routing?: Record<string, unknown>;
}

export interface ReviewShipmentRow {
  id: string;
  dedupeKey: string;
  source: string;
  status: string;
  from: string;
  subject: string;
  receivedAt: string;
  duplicateCount: number;
  updatedAt: string;
  editable: boolean;
  job: ReviewShipmentJob | Record<string, unknown> | null;
  summary: {
    customerCode: string;
    poNumber: string;
    keyMeId: string;
    task: string;
    siteName: string;
    city: string;
    state: string;
    zip: string;
  };
}

export type AiReviewSeverity = "info" | "warn" | "critical";

export interface AiReviewFinding {
  severity: AiReviewSeverity;
  area: string;
  message: string;
  source: string;
  evidence?: string;
}

export interface AiReviewSource {
  key: string;
  label: string;
  online: boolean;
  detail: string;
  lastSignalAt?: string;
}

export interface AiReviewResponse {
  ok: boolean;
  generatedAt: string;
  mode: "rules+evidence";
  findings: AiReviewFinding[];
  sources: AiReviewSource[];
  summary: string;
  readiness: ReviewReadinessSnapshot;
}

export interface ReviewReadinessSnapshot {
  id: string;
  jobHash: string;
  requestXmlHash: string;
  duplicateCheckStatus: string;
  dryRunSuccess: boolean;
  criticalCount: number;
  warnCount: number;
  warningKeys: string[];
  blockers: string[];
  expiresAt: string;
  submitEligible: boolean;
}

export interface SeedTestShipmentResponse {
  ok: boolean;
  inserted: boolean;
  dedupeKey: string;
  detail: string;
}

export interface ShipmentPreviewResponse {
  ok: boolean;
  validationErrors: string[];
  facts: {
    mode: string;
    success: boolean;
    statusText: string;
    warnings: string[];
    errors: string[];
    requestXmlRedacted: string;
  };
  schedulerPreview: {
    columns: string[];
    values: string[];
    byColumn: Record<string, string>;
  };
}

export interface ShipmentSubmitResponse {
  ok: boolean;
  blocked: boolean;
  blockers: string[];
  overrides: {
    factsWriteGate: boolean;
    schedulerWriteGate: boolean;
  };
  facts: null | {
    mode: string;
    success: boolean;
    statusText: string;
    trackingNumber: string;
    quoteTotal: string;
    warnings: string[];
    errors: string[];
    requestXmlRedacted: string;
    responseXml: string;
    duplicateCheck: unknown;
  };
  scheduler: null | {
    requested: boolean;
    skipped: boolean;
    detail: string;
  };
}

export interface SourceReviewResponse {
  ok: boolean;
  generatedAt: string;
  row: {
    id: string;
    dedupeKey: string;
    source: string;
    status: string;
    from: string;
    subject: string;
    messageId: string;
    receivedAt: string;
    duplicateCount: number;
    updatedAt: string;
  };
  sourceEvidence: {
    emailBodyPreview: string;
    attachmentState: "captured_text" | "metadata_only" | "not_captured" | "none_detected";
    attachmentNote: string;
  };
  staged: {
    type: string;
    summary: Record<string, string>;
    normalized: unknown;
    factsPreview: ShipmentPreviewResponse["facts"] | null;
    schedulerPreview: ShipmentPreviewResponse["schedulerPreview"] | null;
  };
  checks: AiReviewFinding[];
  trainingExample: Record<string, unknown>;
}

export interface OttoNotificationRow {
  dedupeKey: string;
  source: string;
  status: string;
  account: string;
  action: string;
  referenceNumber: string;
  siteName: string;
  city: string;
  state: string;
  factsTrackingNumber: string;
  factsTraceUrl: string;
  schedulerAction: string;
  pendingAction: string;
}

export interface OttoNotificationPreview {
  ok: boolean;
  generatedAt: string;
  subject: string;
  to: string[];
  cc: string[];
  rows: OttoNotificationRow[];
  html: string;
  text: string;
  sendConfigured: boolean;
  resendConfigured: boolean;
  provider: "resend";
  from: string;
  windowStart: string;
  windowEnd: string;
  sent?: boolean;
  resendStatus?: number;
  resendResponse?: string;
  resendId?: string;
}

export interface OttoNotificationSettings {
  ok: boolean;
  generatedAt: string;
  source: "d1" | "env";
  from: string;
  to: string[];
  cc: string[];
  includePending: boolean;
  digestWindowMinutes: number;
  sendConfigured: boolean;
  resendConfigured: boolean;
  updatedBy: string;
  updatedAt: string;
}

export interface OttoOperatorAlert {
  id: string;
  dedupeKey: string;
  alertType: string;
  severity: "success" | "warning" | "error" | "info" | string;
  title: string;
  body: string;
  detail: unknown;
  createdAt: string;
  acknowledgedAt: string;
}

export interface CustomerWorkspace {
  customerCode: string;
  customerName: string;
  workspaceState: string;
  focus: string;
  documents: Array<{
    label: string;
    state: string;
    count: string;
    tone: StatusTone;
  }>;
  shipmentTypes: Array<{
    label: string;
    action: string;
    model: string;
    state: string;
    tone: StatusTone;
  }>;
  knowledgeItems: Array<{
    label: string;
    value: string;
    tone: StatusTone;
  }>;
}

export const ottoAdminBaseUrl = "https://hassett-keyme-ingest.deadsetgg.workers.dev";
export const ottoStatusUrl = "https://hassett-keyme-ingest.deadsetgg.workers.dev/otto/public/status";
export const reviewSubmitConfirm = "SUBMIT_REVIEWED_FACTS";
export const notificationSendConfirm = "SEND_OTTO_NOTIFICATION";

export interface EstesShipmentInput {
  source: "estes002";
  customerCode: string;
  dedupeKey: string;
  billToAccount: string;
  billName: string;
  billAddr: string;
  billCity: string;
  billState: string;
  billZip: string;
  billCont: string;
  shipName: string;
  shipAddr: string;
  shipAddr2: string;
  shipCity: string;
  shipState: string;
  shipZip: string;
  shipCont: string;
  shipPhone: string;
  consName: string;
  consAddr: string;
  consAddr2: string;
  consCity: string;
  consState: string;
  consZip: string;
  consCont: string;
  consPhone: string;
  shipDate: string;
  readyTime: string;
  closeTime: string;
  hawb: string;
  waybillNo: string;
  reference: string;
  recoveryCarrier: string;
  recoveryMawb: string;
}

export interface EstesNormalizeResponse {
  ok: boolean;
  mode: "full" | "mandatory_only";
  shipment: EstesShipmentInput;
  changed: boolean;
  notes: string[];
  attentionRequired: string[];
  inServiceArea: boolean;
  detectedRouting: { carrier: string; mawb: string } | null;
}

export interface EstesForwardersResponse {
  billTo: { account: string; name: string };
  lineDrive: { carrier: string; name: string; addr: string; city: string; state: string; zip: string };
  forwarders: Array<{ carrier: string; name: string; addr: string; city: string; state: string; zip: string; aliases: string[] }>;
  quickMenu: Array<{ key: string; label: string }>;
}

export const estesDefaultShipment: EstesShipmentInput = {
  source: "estes002",
  customerCode: "ESTES002",
  dedupeKey: "",
  billToAccount: "ESTES002",
  billName: "ESTES FORWARDING WORLDWIDE",
  billAddr: "",
  billCity: "",
  billState: "",
  billZip: "",
  billCont: "",
  shipName: "",
  shipAddr: "",
  shipAddr2: "",
  shipCity: "",
  shipState: "",
  shipZip: "",
  shipCont: "",
  shipPhone: "",
  consName: "",
  consAddr: "",
  consAddr2: "",
  consCity: "",
  consState: "",
  consZip: "",
  consCont: "",
  consPhone: "",
  shipDate: "",
  readyTime: "",
  closeTime: "",
  hawb: "",
  waybillNo: "",
  reference: "",
  recoveryCarrier: "",
  recoveryMawb: ""
};

export const estesSampleContext = {
  customerCode: "ESTES002",
  subject: "ESTES002 HOUSEBILL recovery - Forward Air PHX",
  body: "Recovery shipment booked under Forward Air MAWB 950-2147892\nPlease recover from FA/PHX and deliver to consignee tomorrow.",
  source: "outlook_ldt"
};

export const latestTrialPreview: TrialPreview = {
  bol: "WO-LTFF1zgygm05i0",
  keyMeId: "SMF-00529-CA-LAW",
  action: "New Install",
  site: "Smart & Final #529, Lawndale CA",
  route: "ANA warehouse to Lawndale site",
  freight: "0 pieces / 1 pallet / 350 lb / 48 x 40 x 76",
  scheduler: "Row waits for accepted FACTS PRO number"
};

export const demoReviewJob: ReviewShipmentJob = {
  source: "hassett-keyme",
  customerCode: "HASSE001",
  dedupeKey: "hassett-keyme|HASSE001|WO-LTFF1zgygm05i0|SMF-00529-CA-LAW|PENDING",
  poNumber: "WO-LTFF1zgygm05i0",
  workOrderId: "WO-LTFF1zgygm05i0",
  keyMeId: "SMF-00529-CA-LAW",
  task: "New Install",
  siteName: "Smart & Final #529",
  address1: "15205 Hawthorne Blvd",
  city: "Lawndale",
  state: "CA",
  zip: "90260",
  storePhone: "(310) 970-5550",
  storeContact: "Smart & Final #529 Manager",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  storeHours: "",
  earliestDate: "2026-05-12",
  latestDate: "2026-05-22",
  marketEta: "2026-05-15",
  suggestedServiceDate: "2026-05-22",
  weekEndingDate: "2026-05-22",
  scheduledDateConfirmed: false,
  serialNumber: "PENDING",
  keyBox: "",
  jobNotes: "KeyMe kiosk install. Use LDT Anaheim origin for CA install.",
  scheduleUrl: "",
  instructionGuideUrl: "",
  airtableUrl: "",
  serviceLinePhone: "",
  serviceLineInstructions: "",
  submissionPortal: "",
  confidence: 100,
  missingRequired: [],
  rawSubject: "KeyMe - SMF-00529-CA-LAW - Lawndale CA",
  rawFrom: "keyme@brendamourmoving.com",
  receivedAt: "2026-05-12T00:00:00.000Z"
};

export const fallbackReviewShipments: ReviewShipmentRow[] = [
  {
    id: "demo-keyme-lawndale",
    dedupeKey: demoReviewJob.dedupeKey,
    source: "hassett-keyme",
    status: "dry_run_ready",
    from: demoReviewJob.rawFrom,
    subject: demoReviewJob.rawSubject,
    receivedAt: demoReviewJob.receivedAt,
    duplicateCount: 0,
    updatedAt: demoReviewJob.receivedAt,
    editable: true,
    job: demoReviewJob,
    summary: {
      customerCode: demoReviewJob.customerCode,
      poNumber: demoReviewJob.poNumber,
      keyMeId: demoReviewJob.keyMeId,
      task: demoReviewJob.task,
      siteName: demoReviewJob.siteName,
      city: demoReviewJob.city,
      state: demoReviewJob.state,
      zip: demoReviewJob.zip
    }
  }
];

export const metrics: Metric[] = [
  { label: "Email Intake", value: "Dry-run", detail: "Cloudflare Email Routing scaffolded", tone: "blue" },
  { label: "Needs Review", value: "Non-KeyMe", detail: "Expanded Hassett actions held", tone: "amber" },
  { label: "FACTS XML", value: "Preflight", detail: "tbolentry4 redacted output only", tone: "green" },
  { label: "Scheduler", value: "Gated", detail: "Graph append disabled", tone: "slate" },
  { label: "Profile Source", value: "Profile + routing", detail: "ANA and AZLDT active", tone: "green" }
];

export const gates: Gate[] = [
  { label: "HASSETT_CUSTOMER_CODE", value: "HASSE001", tone: "green" },
  { label: "FACTS_WRITE_ENABLED", value: "false", tone: "green" },
  { label: "SCHEDULER_WRITE_ENABLED", value: "false", tone: "green" },
  { label: "AI_ROUTE_ENABLED", value: "false", tone: "green" },
  { label: "Profile apply", value: "profile active", tone: "green" },
  { label: "Live deploy verdict", value: "dry-run only", tone: "amber" }
];

export const routeSteps: RouteStep[] = [
  { label: "Email Routing", state: "inbound worker", tone: "blue" },
  { label: "D1 Ledger", state: "dedupe keys", tone: "green" },
  { label: "Queue", state: "retry-safe", tone: "green" },
  { label: "FACTS XML", state: "preflight", tone: "green" },
  { label: "FACTS Submit", state: "gated off", tone: "amber" },
  { label: "Scheduler Row", state: "gated off", tone: "slate" }
];

export const events: EventRow[] = [
  { time: "16:13", lane: "Hassett / KeyMe", event: "Warehouse routing profiles configured", status: "complete", owner: "Codex" },
  { time: "10:01", lane: "Hassett / KeyMe", event: "Profile apply-plan generator pushed", status: "complete", owner: "Codex" },
  { time: "09:59", lane: "Hassett / KeyMe", event: "Proposal BOL preflight ready", status: "review", owner: "Ops" },
  { time: "09:54", lane: "Otto Core", event: "Admin API remains disabled", status: "locked", owner: "Platform" },
  { time: "05:35", lane: "AI Routing", event: "Critical adjudicator route proposed", status: "off", owner: "Platform" },
  { time: "05:30", lane: "Portal", event: "Read-only admin surface scaffolded", status: "shadow", owner: "Platform" },
  { time: "05:25", lane: "ESTES002", event: "ESTES002 normalizer ported from Outlook LDT", status: "complete", owner: "Codex" },
  { time: "05:24", lane: "ESTES002", event: "PHX forwarder dock catalog wired", status: "complete", owner: "Codex" },
  { time: "05:23", lane: "ESTES002", event: "Outlook LDT → Otto bridge endpoint live", status: "shadow", owner: "Platform" }
];

export const evidenceRows: EvidenceRow[] = [
  { source: "Outlook sample", record: "KeyMe Lawndale", confidence: "confirmed", output: "PO, site, timing fields", gate: "parser" },
  { source: "FACTS Replica", record: "KeyMe install freight", confidence: "indicative", output: "0 pcs / 1 pallet / 350 lb / FAK", gate: "resolver" },
  { source: "User confirmation", record: "KeyMe kiosk profile", confidence: "confirmed", output: "1 kiosk / 48 x 40 x 76 / 350 lb", gate: "profile active" },
  { source: "User confirmation", record: "LDT warehouse profiles", confidence: "confirmed", output: "ANA / AZLDT", gate: "routing active" },
  { source: "BOL preflight", record: "tbolentry4 preview", confidence: "structural", output: "HASSE001 locked", gate: "no submit" }
];

export const laneFilters = ["All", "Hassett / KeyMe", "ESTES002", "AI Routing", "Portal"] as const;

export const profileVars: ProfileVar[] = [
  { name: "KEYME_KIOSK_PIECES", value: "0", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_PALLETS", value: "1", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_WEIGHT", value: "350", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_CLASS", value: "FAK", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_LENGTH", value: "48", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_WIDTH", value: "40", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "KEYME_KIOSK_HEIGHT", value: "76", source: "wrangler.jsonc", state: "source active", tone: "green" },
  { name: "LDT_ANAHEIM_FACTS_CODE", value: "ANA", source: "wrangler.jsonc", state: "routing active", tone: "green" },
  { name: "LDT_AZ_FACTS_CODE", value: "AZLDT", source: "wrangler.jsonc", state: "routing active", tone: "green" }
];

export const customerWorkspaces: CustomerWorkspace[] = [
  {
    customerCode: "HASSE001",
    customerName: "Hassett",
    workspaceState: "Active review lane",
    focus: "Special product actions",
    documents: [
      { label: "Outlook action emails", state: "ingested", count: "D1 ledger", tone: "green" },
      { label: "Scheduler workbook rows", state: "pending Graph", count: "read/write gated", tone: "amber" },
      { label: "FACTS BOL outcomes", state: "duplicate checked", count: "write gated", tone: "green" }
    ],
    shipmentTypes: [
      { label: "KeyMe kiosk", action: "install/removal/transfer", model: "deterministic profile", state: "reviewable", tone: "green" },
      { label: "Coinstar", action: "install/removal", model: "needs samples", state: "needs_review", tone: "amber" },
      { label: "Pokemon kiosk", action: "install/removal", model: "needs samples", state: "needs_review", tone: "amber" },
      { label: "Amazon locker", action: "install/removal/replacement", model: "needs samples", state: "needs_review", tone: "amber" }
    ],
    knowledgeItems: [
      { label: "CA installs", value: "ANA origin", tone: "green" },
      { label: "CA removals", value: "site origin / ANA consignee", tone: "green" },
      { label: "AZ actions", value: "AZLDT warehouse", tone: "green" },
      { label: "Other states", value: "AZ team review", tone: "amber" }
    ]
  },
  {
    customerCode: "ESTES002",
    customerName: "Estes Forwarding Worldwide",
    workspaceState: "Active normalization lane",
    focus: "EFW housebill + delivery alert routing",
    documents: [
      { label: "EFW housebill alerts", state: "ported from LDT", count: "9 forwarder docks", tone: "green" },
      { label: "Delivery alerts (recovery)", state: "ported from LDT", count: "carrier + MAWB", tone: "green" },
      { label: "Hotshot dock pickups", state: "ported from LDT", count: "Line Drive dock", tone: "green" }
    ],
    shipmentTypes: [
      { label: "In-service-area", action: "AZ/CA direct delivery", model: "deterministic", state: "ready", tone: "green" },
      { label: "Out-of-area", action: "PHX forwarder routing", model: "rule resolver", state: "ready", tone: "green" },
      { label: "Recovery (DELIVERY ALERT)", action: "carrier dock + MAWB", model: "deterministic", state: "ready", tone: "green" },
      { label: "Manual review", action: "no forwarder detected", model: "operator dispatch", state: "operator owned", tone: "amber" }
    ],
    knowledgeItems: [
      { label: "Bill-to lock", value: "ESTES002 / ESTES FORWARDING WORLDWIDE", tone: "green" },
      { label: "Service area", value: "AZ + CA direct", tone: "green" },
      { label: "Forwarder docks", value: "FA, FX, UAL, AA, DL, WN, AS, UPS, Swissport, LDT", tone: "green" },
      { label: "Contact placeholders", value: "NA / 1234567890", tone: "green" }
    ]
  },
  {
    customerCode: "LDT-TEMPLATE",
    customerName: "Line Drive Template",
    workspaceState: "Template",
    focus: "Reusable validation policy",
    documents: [
      { label: "BOL XML requests", state: "redacted", count: "schema-backed", tone: "green" },
      { label: "Operator corrections", state: "planned", count: "approval gated", tone: "slate" },
      { label: "Customer rules", state: "planned", count: "workspace scoped", tone: "slate" }
    ],
    shipmentTypes: [
      { label: "LTL standard", action: "shipper/consignee", model: "baseline", state: "planned", tone: "slate" },
      { label: "Special product", action: "origin/destination by action", model: "policy resolver", state: "planned", tone: "slate" }
    ],
    knowledgeItems: [
      { label: "Write policy", value: "fail closed", tone: "green" },
      { label: "Secrets", value: "Worker-bound", tone: "green" },
      { label: "AI promotion", value: "approval required", tone: "amber" }
    ]
  }
];
